/**
 * Plugin Rack Component
 * VST-style plugin rack for managing a chain of plugins
 */

import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { DropdownPortal } from '../../ui/dropdown-portal';
import {
  Plus,
  X,
  GripVertical,
  Power,
  ChevronDown,
  ChevronUp,
  Settings2,
  Copy,
  Trash2,
  RotateCcw,
  Activity,
  Sliders,
  Waves,
  Timer,
  Music2,
  Volume2,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { PLUGIN_CATEGORIES, PARAM_TYPES } from './PluginSystem';

// Icon mapping
const CATEGORY_ICONS = {
  dynamics: Activity,
  eq: Sliders,
  modulation: Music2,
  delay: Timer,
  reverb: Waves,
  utility: Volume2,
};

// Color mapping
const CATEGORY_COLORS = {
  dynamics: 'from-red-500 to-orange-500',
  eq: 'from-blue-500 to-cyan-500',
  modulation: 'from-pink-500 to-rose-500',
  delay: 'from-emerald-500 to-teal-500',
  reverb: 'from-purple-500 to-indigo-500',
  utility: 'from-zinc-500 to-zinc-400',
};

/**
 * Parameter Control Component
 */
const ParameterControl = ({ param, value, onChange }) => {
  const handleChange = (newValue) => {
    onChange(param.id, Array.isArray(newValue) ? newValue[0] : newValue);
  };
  
  if (param.type === PARAM_TYPES.BOOL) {
    return (
      <button
        onClick={() => handleChange(!value)}
        className={cn(
          "px-2 py-1 rounded text-xs font-medium transition-colors",
          value ? "bg-cyan-500 text-white" : "bg-zinc-700 text-zinc-400"
        )}
      >
        {param.name}: {value ? 'On' : 'Off'}
      </button>
    );
  }
  
  if (param.type === PARAM_TYPES.ENUM && param.choices) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-[9px] text-zinc-500">{param.name}</label>
        <select
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-white"
        >
          {Object.entries(param.choices).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>
    );
  }
  
  // Default: slider for float/int
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label className="text-[9px] text-zinc-500">{param.name}</label>
        <span className="text-[9px] text-zinc-400 font-mono">
          {param.displayValue ? param.displayValue(value) : `${value.toFixed(2)}${param.unit || ''}`}
        </span>
      </div>
      <Slider
        value={[value]}
        min={param.minValue}
        max={param.maxValue}
        step={param.step || (param.maxValue - param.minValue) / 100}
        onValueChange={([v]) => handleChange(v)}
        className="h-4"
      />
    </div>
  );
};

/**
 * Plugin Slot Component (Individual plugin in rack)
 */
const PluginSlot = ({
  plugin,
  isExpanded,
  onToggleExpand,
  onToggleBypass,
  onRemove,
  onDuplicate,
  onReset,
  onParameterChange,
  isDragging,
}) => {
  const IconComponent = CATEGORY_ICONS[plugin.category] || Activity;
  const colorClass = CATEGORY_COLORS[plugin.category] || 'from-zinc-500 to-zinc-400';
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreBtnRef = useRef(null);
  
  // Get parameters from plugin
  const parameters = [];
  plugin.parameters?.forEach((param, id) => {
    parameters.push({ ...param, id });
  });
  
  return (
    <motion.div
      layout
      className={cn(
        "bg-zinc-800/80 rounded-lg border transition-all",
        plugin.enabled ? "border-zinc-700" : "border-zinc-700/50 opacity-75",
        isDragging && "shadow-xl scale-[1.02] z-50"
      )}
      data-testid={`plugin-slot-${plugin.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 p-2">
        {/* Drag Handle */}
        <div 
          className="cursor-grab active:cursor-grabbing p-1 text-zinc-500 hover:text-zinc-300"
          data-testid={`plugin-drag-${plugin.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </div>
        
        {/* Plugin Icon */}
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br",
          colorClass,
          !plugin.enabled && "grayscale"
        )}>
          <IconComponent className="w-4 h-4 text-white" />
        </div>
        
        {/* Plugin Name */}
        <div className="flex-1 min-w-0">
          <div className={cn(
            "text-sm font-medium truncate",
            plugin.enabled ? "text-white" : "text-zinc-500"
          )}>
            {plugin.name}
          </div>
          <div className="text-[10px] text-zinc-500 capitalize truncate">
            {plugin.category}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          {/* Bypass Toggle */}
          <button
            onClick={() => onToggleBypass(plugin.id)}
            className={cn(
              "w-7 h-7 rounded flex items-center justify-center transition-colors",
              plugin.enabled 
                ? "bg-emerald-500 hover:bg-emerald-400 text-white" 
                : "bg-zinc-700 hover:bg-zinc-600 text-zinc-400"
            )}
            title={plugin.enabled ? "Bypass" : "Enable"}
            data-testid={`plugin-bypass-${plugin.id}`}
          >
            <Power className="w-3.5 h-3.5" />
          </button>
          
          {/* Expand/Collapse */}
          <button
            onClick={() => onToggleExpand(plugin.id)}
            className="w-7 h-7 rounded bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center text-zinc-300"
            title={isExpanded ? "Collapse" : "Expand"}
            data-testid={`plugin-expand-${plugin.id}`}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {/* More Options Menu */}
          <div className="relative">
            <button
              ref={moreBtnRef}
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="w-7 h-7 rounded bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center text-zinc-300"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </button>
            
            {/* Dropdown via portal */}
            <DropdownPortal
              triggerRef={moreBtnRef}
              isOpen={showMoreMenu}
              onClose={() => setShowMoreMenu(false)}
              align="right"
              width={128}
            >
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden">
                <button
                  onClick={() => { onDuplicate?.(plugin.id); setShowMoreMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-700 transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  Duplicate
                </button>
                <button
                  onClick={() => { onReset?.(plugin.id); setShowMoreMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-700 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </button>
                <hr className="border-zinc-700 my-1" />
                <button
                  onClick={() => { onRemove(plugin.id); setShowMoreMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  Remove
                </button>
              </div>
            </DropdownPortal>
          </div>
        </div>
      </div>
      
      {/* Expanded Parameters */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-1 border-t border-zinc-700/50 space-y-3">
              {/* Presets */}
              {plugin.presets && plugin.presets.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {plugin.presets.map(preset => (
                    <button
                      key={preset.id}
                      onClick={() => plugin.loadPreset?.(preset.id)}
                      className={cn(
                        "px-2 py-0.5 rounded text-[10px] font-medium transition-colors",
                        plugin.currentPreset === preset.id
                          ? "bg-cyan-500 text-white"
                          : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                      )}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Parameters Grid */}
              <div className="grid grid-cols-2 gap-3">
                {parameters.map(param => (
                  <ParameterControl
                    key={param.id}
                    param={param}
                    value={param.value}
                    onChange={(paramId, value) => onParameterChange?.(plugin.id, paramId, value)}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Main Plugin Rack Component
 */
export const PluginRack = ({
  plugins = [],
  onAddPlugin,
  onRemovePlugin,
  onToggleBypass,
  onReorderPlugins,
  onParameterChange,
  onDuplicatePlugin,
  onResetPlugin,
  className,
}) => {
  const [expandedPlugins, setExpandedPlugins] = useState(new Set());
  const [draggingId, setDraggingId] = useState(null);
  
  const toggleExpand = useCallback((pluginId) => {
    setExpandedPlugins(prev => {
      const next = new Set(prev);
      if (next.has(pluginId)) {
        next.delete(pluginId);
      } else {
        next.add(pluginId);
      }
      return next;
    });
  }, []);
  
  return (
    <div className={cn("flex flex-col h-full", className)} data-testid="plugin-rack">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
        <span className="text-sm font-medium text-white">Plugin Chain</span>
        <Button
          onClick={onAddPlugin}
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          data-testid="add-plugin-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add
        </Button>
      </div>
      
      {/* Plugin List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {plugins.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <div className="text-sm mb-2">No plugins loaded</div>
            <Button
              onClick={onAddPlugin}
              size="sm"
              variant="outline"
              className="text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Plugin
            </Button>
          </div>
        ) : (
          <Reorder.Group
            axis="y"
            values={plugins}
            onReorder={onReorderPlugins}
            className="space-y-2"
          >
            {plugins.map((plugin) => (
              <Reorder.Item
                key={plugin.id}
                value={plugin}
                onDragStart={() => setDraggingId(plugin.id)}
                onDragEnd={() => setDraggingId(null)}
                className="cursor-grab active:cursor-grabbing"
              >
                <PluginSlot
                  plugin={plugin}
                  isExpanded={expandedPlugins.has(plugin.id)}
                  onToggleExpand={toggleExpand}
                  onToggleBypass={onToggleBypass}
                  onRemove={onRemovePlugin}
                  onDuplicate={onDuplicatePlugin}
                  onReset={onResetPlugin}
                  onParameterChange={onParameterChange}
                  isDragging={draggingId === plugin.id}
                />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}
      </div>
      
      {/* Footer Stats */}
      {plugins.length > 0 && (
        <div className="px-3 py-2 border-t border-zinc-800 text-[10px] text-zinc-500">
          {plugins.length} plugin{plugins.length !== 1 ? 's' : ''} • {plugins.filter(p => p.enabled).length} active
        </div>
      )}
    </div>
  );
};

export default PluginRack;
